import { useState, useEffect, useRef, useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { getSharedMap } from '../utils/share';
import { getNodeColors } from '../utils/colors';
import { CANVAS_SIZE, CONNECTION, NODE_W, NODE_H, NODE_W_ROOT, NODE_H_ROOT, NODE_RX } from '../constants';

const POSTIT_COLORS = {
  yellow: { fill: '#fef9c3', stroke: '#d97706', text: '#92400e' },
  green:  { fill: '#dcfce7', stroke: '#16a34a', text: '#14532d' },
  red:    { fill: '#fee2e2', stroke: '#ef4444', text: '#7f1d1d' },
};
const POSTIT_HANDLE_H = 22;
const POSTIT_W_DEFAULT = 200;
const POSTIT_H_DEFAULT = 140;
const FONT = "'Inter', system-ui, -apple-system, sans-serif";

const calcBezier = (fromNode, toNode) => {
  const dx = toNode.x - fromNode.x;
  const W_from = fromNode.type === 'problem' ? NODE_W_ROOT : NODE_W;
  const W_to = toNode.type === 'problem' ? NODE_W_ROOT : NODE_W;

  const fromPt = dx >= 0
    ? { x: fromNode.x + W_from / 2, y: fromNode.y }
    : { x: fromNode.x - W_from / 2, y: fromNode.y };

  const toPt = dx >= 0
    ? { x: toNode.x - W_to / 2, y: toNode.y }
    : { x: toNode.x + W_to / 2, y: toNode.y };

  const tension = Math.abs(dx) * 0.45;
  const cp1 = { x: fromPt.x + (dx >= 0 ? tension : -tension), y: fromPt.y };
  const cp2 = { x: toPt.x - (dx >= 0 ? tension : -tension), y: toPt.y };

  return `M ${fromPt.x} ${fromPt.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${toPt.x} ${toPt.y}`;
};

export const SharedMapView = ({ shareId }) => {
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [mapData, setMapData] = useState(null);
  const [mapName, setMapName] = useState('');
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  const isPanningRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  useEffect(() => {
    getSharedMap(shareId)
      .then((data) => {
        setMapName(data.map_name);
        setMapData(data.map_data);
        setStatus('ok');
        setTimeout(() => {
          const container = containerRef.current;
          const nodes = data.map_data.nodes || [];
          if (!container || nodes.length === 0) return;
          const xs = nodes.map((n) => n.x);
          const ys = nodes.map((n) => n.y);
          const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
          const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
          container.scrollLeft = cx * zoomRef.current - container.clientWidth / 2;
          container.scrollTop = cy * zoomRef.current - container.clientHeight / 2;
        }, 100);
      })
      .catch(() => setStatus('error'));
  }, [shareId]);

  // Mouse wheel zoom
  useEffect(() => {
    if (status !== 'ok') return;
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e) => {
      e.preventDefault();
      const cur = zoomRef.current;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const newZoom = Math.min(3, Math.max(0.2, cur * factor));
      const rect = container.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const svgX = (mx + container.scrollLeft) / cur;
      const svgY = (my + container.scrollTop) / cur;
      setZoom(newZoom);
      requestAnimationFrame(() => {
        container.scrollLeft = svgX * newZoom - mx;
        container.scrollTop = svgY * newZoom - my;
      });
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [status]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    isPanningRef.current = true;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isPanningRef.current) return;
    const container = containerRef.current;
    if (!container) return;
    container.scrollLeft -= e.clientX - lastPosRef.current.x;
    container.scrollTop -= e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const stopPan = useCallback(() => { isPanningRef.current = false; }, []);

  if (status === 'loading') {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="flex items-center gap-3" style={{ color: 'var(--text-muted)' }}>
          <span className="w-5 h-5 rounded-full border-2 animate-spin" style={{ borderColor: 'var(--border)', borderTopColor: 'var(--accent-2)' }} />
          <span className="text-[15px]">Carico la mappa…</span>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center">
          <p className="text-[18px] font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Mappa non trovata.</p>
          <p className="text-[14px] mb-4" style={{ color: 'var(--text-muted)' }}>Il link potrebbe essere errato o rimosso.</p>
          <a href={window.location.origin} className="hover:underline text-[14px] font-medium" style={{ color: 'var(--accent-2)' }}>
            Vai all'app →
          </a>
        </div>
      </div>
    );
  }

  const { nodes = [], connections = [], postIts = [] } = mapData;

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'var(--bg)' }}>
      {/* Banner sola lettura */}
      <div className="flex items-center justify-between px-5 py-2.5 backdrop-blur-sm border-b shrink-0 z-10" style={{ background: 'var(--bg-dim)', borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-3">
          <span className="text-[13px] font-semibold truncate max-w-xs" style={{ color: 'var(--text-primary)' }}>{mapName}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full shrink-0" style={{ background: 'var(--surface)', color: 'var(--text-dim)', border: '1px solid var(--border)' }}>
            sola lettura
          </span>
        </div>
        <a
          href={window.location.origin}
          className="flex items-center gap-1.5 text-[13px] font-medium transition-colors shrink-0"
          style={{ color: 'var(--accent-2)' }}
        >
          Apri nell'app <ExternalLink size={13} />
        </a>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopPan}
        onMouseLeave={stopPan}
      >
        <svg
          width={CANVAS_SIZE * zoom}
          height={CANVAS_SIZE * zoom}
          style={{ display: 'block', background: 'var(--bg)' }}
        >
          <g transform={`scale(${zoom})`}>
            {/* Connessioni bezier */}
            {connections.map((conn) => {
              const from = nodes.find((n) => n.id === conn.from);
              const to = nodes.find((n) => n.id === conn.to);
              if (!from || !to) return null;
              const d = calcBezier(from, to);
              const opacity = from.banned || to.banned ? CONNECTION.BANNED_OPACITY : CONNECTION.OPACITY;
              return (
                <path
                  key={conn.id}
                  d={d}
                  stroke={conn.color || CONNECTION.COLOR}
                  strokeWidth={CONNECTION.WIDTH}
                  fill="none"
                  opacity={opacity}
                />
              );
            })}

            {/* Nodi */}
            {nodes.map((node) => {
              const { fillColor, textColor, outlineColor, outlineWidth } = getNodeColors(node);
              const W = node.type === 'problem' ? NODE_W_ROOT : NODE_W;
              const H = node.type === 'problem' ? NODE_H_ROOT : NODE_H;
              const rx = node.x - W / 2;
              const ry = node.y - H / 2;
              return (
                <g key={node.id} opacity={node.banned ? 0.5 : 1}>
                  <rect
                    x={rx} y={ry}
                    width={W} height={H}
                    rx={NODE_RX}
                    fill={fillColor}
                    stroke={outlineColor}
                    strokeWidth={outlineWidth}
                  />
                  <foreignObject x={rx + 10} y={ry} width={W - 20} height={H}>
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        height: '100%', textAlign: 'center',
                        color: textColor,
                        fontSize: '11px', fontWeight: '700',
                        textTransform: 'uppercase',
                        lineHeight: '1.2', letterSpacing: '0.02em',
                        fontFamily: FONT,
                        textDecoration: node.banned ? 'line-through' : 'none',
                        padding: '0 4px',
                        wordBreak: 'break-word',
                      }}
                    >
                      {node.title}
                    </div>
                  </foreignObject>
                </g>
              );
            })}

            {/* Post-it — sopra i nodi */}
            {postIts.map((postIt) => {
              const c = POSTIT_COLORS[postIt.color] || POSTIT_COLORS.yellow;
              const w = postIt.w || POSTIT_W_DEFAULT;
              const h = postIt.h || POSTIT_H_DEFAULT;
              const { x, y } = postIt;
              const innerH = h - POSTIT_HANDLE_H - 8;
              return (
                <g key={postIt.id}>
                  <rect x={x + 3} y={y + 5} width={w} height={h} fill="rgba(0,0,0,0.07)" />
                  <rect x={x} y={y} width={w} height={h} fill={c.fill} stroke={c.stroke} strokeWidth={0.8} />
                  <rect x={x} y={y} width={w} height={POSTIT_HANDLE_H} fill={`${c.stroke}18`} />
                  {postIt.text && (
                    <foreignObject x={x + 12} y={y + POSTIT_HANDLE_H + 4} width={w - 24} height={innerH}>
                      <div style={{
                        color: c.text, fontSize: '13px', lineHeight: '1.5',
                        fontFamily: FONT, overflow: 'hidden',
                        wordBreak: 'break-word', whiteSpace: 'pre-wrap', height: '100%',
                      }}>
                        {postIt.text}
                      </div>
                    </foreignObject>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
};
